import React, { useState } from "react";

import { msg, t } from "@lingui/core/macro";
import { useLingui } from "@lingui/react";
import { Trans } from "@lingui/react/macro";

import Typography from "@mui/material/Typography";

import { StickyContainer } from "@readai/common/components/styles";
import Tab from "@readai/common/components/Tab";
import Tabs from "@readai/common/components/Tabs";
import TooltipIcon from "@readai/common/components/TooltipIcon";
import useAuth from "@readai/common/hooks/useAuth";
import Stack, { Row } from "@readai/pulse/components/Stack";

import * as Events from "@app/booking/tracking/events";

import BestTimes from "./BestTimes";
import DateAndTime from "./DateAndTime";
import { BestTimesBanner } from "./styles";
import { Header, Heading, TabsContainer } from "../styles";

const TABS = [
  { value: "all", label: msg`Date & time` },
  { value: "best", label: msg`Best options` },
];

const SelectDateAndTime = ({
  ownerName,
  onContinue,
}: {
  ownerName?: string;
  onContinue?: () => void;
}) => {
  const { _ } = useLingui();
  const { user } = useAuth();
  const [selectedTab, setSelectedTab] = useState("all");

  const handleChangeTab = (_event: React.SyntheticEvent, value: string) => {
    if (value === "best") {
      Events.viewBestTimes();
    }
    setSelectedTab(value);
  };

  const showBestTimesBanner = Boolean(user) && ownerName;

  return (
    <Stack
      alignItems="center"
      spacing={2}
      flexGrow={1}
      sx={{ textAlign: "center", width: "100%" }}
    >
      <StickyContainer
        width="100%"
        sx={{ position: { xs: "relative", lg: "sticky" } }}
      >
        <Header spacing={2}>
          <Row variant="center" spacing={1}>
            <Heading>
              <Trans>Select a date and time</Trans>
            </Heading>
            <TooltipIcon
              title={t`Read recommends meeting times based on promoting factors like engagement, punctuality, and focus time.`}
            />
          </Row>
          <TabsContainer variant="center">
            <Tabs
              value={selectedTab}
              onChange={handleChangeTab}
              sx={{ width: "100%" }}
              variant="fullWidth"
            >
              {TABS.map((tab) => (
                <Tab key={tab.value} label={_(tab.label)} value={tab.value} />
              ))}
            </Tabs>
          </TabsContainer>
        </Header>
      </StickyContainer>
      {showBestTimesBanner && (
        <BestTimesBanner>
          <Typography variant="bodySecondaryReg">
            <Trans>
              ⚡️ Read is finding the best times for both you and {ownerName}{" "}
              based on your calendars
            </Trans>
          </Typography>
        </BestTimesBanner>
      )}
      <Stack paddingTop={showBestTimesBanner ? 0 : 1} width="100%" flex={1}>
        {selectedTab === "all" ? (
          <DateAndTime onSelectTime={onContinue} />
        ) : (
          <BestTimes onSelectTime={onContinue} />
        )}
      </Stack>
    </Stack>
  );
};

export default SelectDateAndTime;
